import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  RefreshCw,
  Eye,
  MoreHorizontal,
  Award,
  TrendingUp,
  CheckCircle2,
  XCircle,
  FilePlus,
  UserCheck,
  UserX,
  X,
  ClipboardList
} from 'lucide-react';

// Common Components
import { useToast, ToastContainer } from '../common/Toast';
import DataTable from '../common/DataTable';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

// Services
import SourcingAwardService from '../../services/sourcingAwardService';
import SourcingRfpService from '../../services/sourcingRFPService';

//////////////////////////////////////////////////////////////
// HELPER: MENU ITEM
//////////////////////////////////////////////////////////////

const MenuItem = ({ icon, label, onClick, danger }) => (
  <button
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    className={`
      flex items-center gap-2
      w-full px-3 py-2 text-sm
      transition-colors duration-150
      ${danger
        ? "text-red-600 hover:bg-red-50"
        : "text-gray-700 hover:bg-gray-100"}
    `}
  >
    <span className={danger ? "text-red-500" : "text-gray-400"}>
      {icon}
    </span>
    {label}
  </button>
);
//////////////////////////////////////////////////////////////
// COMPONENT: ACTION DROPDOWN
//////////////////////////////////////////////////////////////

const AwardActionDropdown = ({ response, onAward, onViewEvaluation, onView, onCreateContract }) => {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  // Close dropdown on outside click or scroll
  useEffect(() => {
    const close = (e) => {
      if (!menuRef.current?.contains(e.target) && !buttonRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener("mousedown", close);
      window.addEventListener("scroll", close, true);
    }
    return () => {
      document.removeEventListener("mousedown", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [open]);

  const rect = buttonRef.current?.getBoundingClientRect();

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setOpen((prev) => !prev)}
        className={`
          flex items-center justify-center
          w-8 h-8 rounded-md border border-gray-200
          transition-all shadow-sm
          ${open ? 'bg-gray-100 border-gray-400' : 'bg-white hover:bg-gray-50'}
        `}
      >
        <MoreHorizontal size={16} className={open ? 'text-blue-600' : 'text-gray-600'} />
      </button>

      {open && rect && createPortal(
        <div
          ref={menuRef}
          style={{
            position: "fixed",
            top: rect.bottom + 6,
            left: rect.left - 144,
            zIndex: 9999
          }}
          className="w-48 bg-white border border-gray-200 rounded-lg shadow-xl py-1 animate-in fade-in zoom-in duration-100"
        >
          {/* Always visible: View Option */}
          <MenuItem
            icon={<Eye size={16} />}
            label="View Response"
            onClick={() => { setOpen(false); onView(response); }}
          />

          {response.status === "EVALUATED" && (
            <>
              <div className="border-t my-1" />
              <MenuItem
                icon={<UserCheck size={16} className="text-green-600" />}
                label="Award Vendor"
                onClick={() => { setOpen(false); onAward(response); }}
              />
              {/* NEW VIEW EVALUATION BUTTON */}
              <MenuItem
                icon={<ClipboardList size={16} className="text-blue-600" />}
                label="View Evaluation"
                onClick={() => { setOpen(false); onViewEvaluation(response); }}
              />
            </>
          )}

          {/* Contract generation logic remains for AWARDED status */}
          {response.status === "AWARDED" && (
            <>
              <div className="border-t my-1" />
              <MenuItem
                icon={<FilePlus size={16} className="text-blue-600" />}
                label="Create Contract"
                onClick={() => { setOpen(false); onCreateContract(response); }}
              />
            </>
          )}
        </div>,
        document.body
      )}
    </>
  );
};

const getStatusPill = (status) => {
  const config = {
    SUBMITTED: { bg: '#eff6ff', color: '#1d4ed8', dot: '#3b82f6', label: 'Under Review' },
    AWARDED: { bg: '#d1fae5', color: '#059669', dot: '#10b981', label: 'Awarded' },
    REJECTED: { bg: '#fee2e2', color: '#dc2626', dot: '#ef4444', label: 'Rejected' },
  };
  const s = config[status] || { bg: '#f3f4f6', color: '#6b7280', dot: '#6b7280', label: status };

  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 10px',
      borderRadius: '999px', fontSize: '11px', fontWeight: 600, background: s.bg, color: s.color,
      border: `1px solid ${s.color}20`, textTransform: 'uppercase'
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.dot }} />
      {s.label}
    </span>
  );
};

//////////////////////////////////////////////////////////////
// MAIN COMPONENT
//////////////////////////////////////////////////////////////

const AwardManagement = () => {
  const navigate = useNavigate();
  const { success, error: showError, toasts, removeToast } = useToast();

  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [availableRfps, setAvailableRfps] = useState([]);
  const [selectedRfpId, setSelectedRfpId] = useState('');
  const [viewEvalModal, setViewEvalModal] = useState({ open: false, data: null });

  const loadData = async () => {
    setLoading(true);
    try {
      const rfpRes = await SourcingRfpService.getRfps();
      setAvailableRfps(rfpRes.data || []);

      const responseRes = await SourcingRfpService.getResponses(selectedRfpId);
      setResponses(responseRes.data || []);
    } catch (err) {
      showError("Could not load sourcing data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [selectedRfpId]);

  // ACTION: Award Vendor
  const handleAwardVendor = async (res) => {
    const justification = window.prompt("Enter justification for awarding this vendor:");
    if (!justification) return; // User cancelled

    try {
      await SourcingRfpService.recommendAward(res.rfpId?._id || res.rfpId, {
        winningResponseId: res._id, // Match the service key name
        justification: justification, // Add this required field
        createdBy: "System User" // Or your user state ID
      });

      success("Award recommendation created! Awaiting approval.");
      loadData();
    } catch (err) {
      showError(err.message || "Failed to recommend award");
    }
  };
  
  const handleViewEvaluation = (res) => {
  if (!res.evaluationId) {
    showError("No evaluation data found for this response.");
    return;
  }
  setViewEvalModal({ open: true, data: res.evaluationId });
};

  // ACTION: Create Contract (Uses Award Service)
  const handleCreateContract = async (res) => {
    try {
      const contractRes = await SourcingAwardService.createContractPrefill(res._id);
      success("Contract draft generated successfully!");
      // navigate(`/contracts/edit/${contractRes.data._id}`);
    } catch (err) {
      showError(err.message || "Could not generate contract");
    }
  };

  const columns = [
    {
      key: '__spacer__',
      label: '',
      width: '16px',
      sortable: false,
      render: () => <div style={{ width: '16px' }} />
    },
    {
      key: 'rfpTitle',
      label: 'Sourcing Event',
      render: (_, res) => (
        <div className="flex flex-col">
          <span className="font-semibold text-gray-800">
            {res.rfpId?.title || 'N/A'}
          </span>
          <span className="text-[10px] text-[var(--gold-primary)] font-bold uppercase">
            {res.rfpId?.rfpNumber || 'No ID'}
          </span>
        </div>
      )
    },
    {
    key: 'rfpTitle',
    label: 'Sourcing Event',
    render: (_, res) => (
      <div className="flex flex-col">
        <span className="font-semibold text-gray-800">
          {res.rfpId?.title || 'N/A'}
        </span>
        <span className="text-[10px] text-blue-600 font-bold uppercase">
          {res.rfpId?.rfpNumber || 'No ID'}
        </span>
      </div>
    )
  },
    {
      key: 'vendor',
      label: 'Vendor Name',
      render: (_, res) => (
        <div className="flex flex-col">
          <span className="font-semibold text-gray-900">{res.vendorId?.name || 'Unknown Vendor'}</span>
          <span className="text-[10px] text-gray-400 uppercase">Response: {res._id?.slice(-6)}</span>
        </div>
      )
    },
    {
      key: 'value',
      label: 'Bid Amount',
      render: (_, res) => {
        const total = res.lineItems?.reduce((sum, item) => sum + (item.price || 0), 0);
        return <div className="font-mono font-bold text-gray-800">${total?.toLocaleString()}</div>;
      }
    },
    {
      key: 'status',
      label: 'Status',
      render: (_, res) => getStatusPill(res.status)
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '80px',
      render: (_, res) => (
        <AwardActionDropdown
          response={res}
          onAward={handleAwardVendor}
          onViewEvaluation={handleViewEvaluation}
          onCreateContract={handleCreateContract}
          onView={(r) => navigate(`/sourcing/responses/view/${r._id}`)}
        />
      )
    }
  ];

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title="Award Management"
        description="Review vendor submissions and convert approved bids into contracts"
        actions={[{ icon: <RefreshCw size={18} />, variant: 'outline', onClick: loadData }]}
      />

      <div className="page-container">
        {/* RFP FILTER */}
        <div className="flex items-center gap-4 bg-white p-4 rounded-xl border border-gray-200 mb-6 shadow-sm">
          <div className="p-2 bg-[#FFF9E6] text-[var(--gold-primary)] rounded-lg"><Award size={20} /></div>
          <div className="flex-1">
            <label className="block text-[10px] font-bold text-gray-400 uppercase">Sourcing Event</label>
            <select
              value={selectedRfpId}
              onChange={(e) => setSelectedRfpId(e.target.value)}
              className="w-full max-w-md bg-transparent border-none text-gray-900 font-semibold focus:ring-0 cursor-pointer p-0"
            >
              <option value="">All Active RFPs</option>
              {availableRfps.map(rfp => (
                <option key={rfp._id} value={rfp._id}>{rfp.rfpNumber} - {rfp.title}</option>
              ))}
            </select>
          </div>
        </div>

        {viewEvalModal.open && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
              <div className="p-6 border-b bg-gray-50 flex justify-between items-center">
                <h3 className="text-lg font-bold text-gray-800">Evaluation Details</h3>
                <button onClick={() => setViewEvalModal({ open: false, data: null })} className="text-gray-400 hover:text-gray-600">
                  <X size={20} />
                </button>
              </div>
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-[#FFF9E6] rounded-xl border border-[#FFE8A3]">
                    <p className="text-xs text-[var(--gold-primary)] font-bold uppercase">Technical</p>
                    <p className="text-2xl font-black text-[#8C6D1F]">{viewEvalModal.data.technicalScore}/100</p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-xl border border-green-100">
                    <p className="text-xs text-green-600 font-bold uppercase">Commercial</p>
                    <p className="text-2xl font-black text-green-900">{viewEvalModal.data.commercialScore}/100</p>
                  </div>
                </div>
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                  <p className="text-xs text-gray-500 font-bold uppercase mb-2">Evaluator Comments</p>
                  <p className="text-gray-700 text-sm italic">"{viewEvalModal.data.comments || 'No comments provided.'}"</p>
                </div>
              </div>
              <div className="p-4 bg-gray-50 border-t flex justify-end">
                <button
                  onClick={() => setViewEvalModal({ open: false, data: null })}
                  className="px-6 py-2 bg-gray-800 text-white rounded-lg font-semibold text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* STATS */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-500 font-medium">Bids to Review</div>
              <div className="text-2xl font-bold text-gray-900">{responses.filter(r => r.status === 'SUBMITTED').length}</div>
            </div>
            <div className="text-orange-500 bg-orange-50 p-3 rounded-lg"><TrendingUp size={24} /></div>
          </div>
          <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-500 font-medium">Awarded Bids</div>
              <div className="text-2xl font-bold text-green-600">{responses.filter(r => r.status === 'AWARDED').length}</div>
            </div>
            <div className="text-green-600 bg-green-50 p-3 rounded-lg"><CheckCircle2 size={24} /></div>
          </div>
          <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-500 font-medium">Rejected Bids</div>
              <div className="text-2xl font-bold text-red-500">{responses.filter(r => r.status === 'REJECTED').length}</div>
            </div>
            <div className="text-red-500 bg-red-50 p-3 rounded-lg"><XCircle size={24} /></div>
          </div>
        </div>

        <DataTable
          data={responses}
          columns={columns}
          loading={loading}
          showActions={false}
          emptyMessage="No vendor responses found for this selection."
        />
      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default AwardManagement;